import React, { useState, useEffect } from 'react';
import { MainLayout } from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertCircle, CheckCircle, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Package, CalendarDays } from 'lucide-react';
import { categories as categoriesApi, products as productsApi } from '@/lib/api';
import { toast } from 'sonner';

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', available_days: null });
  const [validationStatus, setValidationStatus] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [catRes, prodRes] = await Promise.all([
        categoriesApi.list(),
        productsApi.list()
      ]);
      setCategories(catRes.data || []);
      setProducts(prodRes.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingCategory) {
        await categoriesApi.update(editingCategory.id, {
          ...formData,
          available_days: formData.available_days ? JSON.stringify(formData.available_days) : null
        });
        toast.success('Categoria atualizada');
      } else {
        await categoriesApi.create({
          ...formData,
          available_days: formData.available_days ? JSON.stringify(formData.available_days) : null
        });
        toast.success('Categoria criada');
      }
      setIsDialogOpen(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', available_days: null });
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar categoria');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return;
    try {
      await categoriesApi.delete(id);
      toast.success('Categoria excluída');
      loadData();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Erro ao excluir categoria';
      toast.error(msg);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ 
      name: category.name, 
      description: category.description || '',
      available_days: category.available_days ? JSON.parse(category.available_days) : null
    });
    setIsDialogOpen(true);
  };

  const handleProductCategoryChange = async (productId, categoryId) => {
    try {
      await productsApi.update(productId, { category_id: categoryId });
      toast.success('Produto atualizado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar produto');
    }
  };

  const validate99Food = async () => {
    const emptyCategories = categories.filter(cat => 
      !products.some(p => p.category_id === cat.id)
    );
    
    if (emptyCategories.length > 0) {
      setValidationStatus({ 
        valid: false, 
        message: `Categorias sem produtos: ${emptyCategories.map(c => c.name).join(', ')}`
      });
    } else {
      setValidationStatus({ valid: true, message: 'Cardápio pronto para sincronização' });
    }
  };

  const getCategoryProducts = (categoryId) => {
    return products.filter(p => p.category_id === categoryId);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Carregando...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>Categorias</h1>
            <p className="text-slate-500">Gerencie as categorias do cardápio para 99Food</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={validate99Food}>
              <AlertCircle className="w-4 h-4 mr-2" />
              Validar 99Food
            </Button>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingCategory(null); setFormData({ name: '', description: '', available_days: null }); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Categoria
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingCategory ? 'Editar Categoria' : 'Nova Categoria'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <Input 
                    placeholder="Nome da categoria" 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                  <Input 
                    placeholder="Descrição (opcional)" 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                  <Button onClick={handleSave} className="w-full">Salvar</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {validationStatus && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${validationStatus.valid ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
            {validationStatus.valid ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {validationStatus.message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Categorias ({categories.length})</CardTitle>
              <CardDescription>Use as setas para reordenar</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {categories.map((category, index) => (
                  <div key={category.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col">
                        <button className="p-1 hover:bg-slate-200 rounded" disabled={index === 0}>
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button className="p-1 hover:bg-slate-200 rounded" disabled={index === categories.length - 1}>
                          <ArrowDown className="w-4 h-4" />
                        </button>
                      </div>
                      <div>
                        <p className="font-medium">{category.name}</p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          {getCategoryProducts(category.id).length} produtos
                        </p>
                        {category.available_days && (() => {
                          try {
                            const days = JSON.parse(category.available_days);
                            const dayLabels = { 0: 'Seg', 1: 'Ter', 2: 'Qua', 3: 'Qui', 4: 'Sex', 5: 'Sab', 6: 'Dom' };
                            return (
                              <p className="text-xs text-red-600 flex items-center gap-1">
                                <CalendarDays className="w-3 h-3" />
                                {days.map(d => dayLabels[d]).join(', ')}
                              </p>
                            );
                          } catch { return null; }
                        })()}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(category)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(category.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vincular Produtos</CardTitle>
              <CardDescription>Selecione a categoria de cada produto</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {products.map(product => (
                  <div key={product.id} className="flex items-center justify-between p-2 border-b">
                    <div>
                      <p className="font-medium text-sm">{product.name}</p>
                      <p className="text-xs text-slate-500">
                        {categories.find(c => c.id === product.category_id)?.name || 'Sem categoria'}
                      </p>
                    </div>
                    <Select 
                      value={product.category_id || ''} 
                      onValueChange={(value) => handleProductCategoryChange(product.id, value)}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
